import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Fade from '@mui/material/Fade';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { useTranslation } from 'react-i18next';

// Local imports
import { useAuth } from "../AuthContext";
import { ERROR_MESSAGES } from "../../api/constants";
import { api } from '../../api';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    height: '80%',
    overflow: 'scroll',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    width: {
      xs: '80%',
      sm: '60%',
      md: 400,
    }
};

const imgBoxStyles = {
    width: { sm: '50%', md: '70%' },
    height: { xs: '50%', sm: '-webkit-fill-available' },
    backgroundImage: "url(/images/bg-ai-image1.png)", 
    backgroundSize: 'cover', 
    borderTopLeftRadius: 8, 
    borderBottomLeftRadius: {
        xs: 0,
        sm: 8
    }, 
    borderTopRightRadius: {
        xs: 8,
        sm: 0
    },
    display: 'grid', 
    placeItems: 'center'
};

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
  
    const { isLoggedIn, setIsLoggedIn, isAdmin, setIsAdmin } = useAuth();
    const from = location.state?.from?.pathname || '/portal';

    useEffect(() => {
        // Redirect user if already is logged in
        if (isLoggedIn) {
            isAdmin ? navigate('/dashboard') : navigate(from, { replace: true });
        }
    }, [isLoggedIn, isAdmin, from, navigate]);
  
    const loginSubmit = async (e) => {
      e.preventDefault();
      try {
        const data = await api.post('/auth/login', { username, password });
        if (data.userRole) {
            setIsLoggedIn(true);
            setIsAdmin(data.userRole === 'admin');
        } else {
            setError("Error: Missing user role");
        }
      } catch (err) {
        // Handle different error messages
        if (err.message === ERROR_MESSAGES.INVALID_CREDENTIALS) {
            setError(t('login.401_error'));
            setUsername('');
            setPassword('');
        } else {
            setError(t('login.500_error'));
        }
      }
    };

    const handleTogglePassword = () => setShowPassword((prev) => !prev);
  
    return (
        <Fade in timeout={1000}>
            <div className="main">
                <Box
                    sx={{
                    marginTop: 10,
                    display: 'flex',
                    flexDirection: {
                        xs: 'column',
                        sm: 'row'
                    },
                    borderRadius: 2,
                    boxShadow: 3,
                    width: {
                        xs: '100%',
                        sm: '90%',
                        md: '80%'
                    },
                    height: '35rem',
                    minHeight: 'fit-content',
                    margin: 'auto',
                    }}
                >
                    
                    <Box sx={imgBoxStyles}>
                        <Typography 
                            variant="h3"
                            sx={{
                                textAlign: 'center', 
                                marginTop: 5,
                                color: '#ffffff',
                                width: {
                                    xs: '70%',
                                    sm: '80%',
                                    md: '70%',
                                },
                                margin: 'auto',
                                fontWeight: '600',
                                padding: 2,
                                fontSize: {
                                    xs: '1.5rem',
                                    sm: '2rem',
                                    md: '3rem'
                                }
                            }}>
                            {t('login.welcome')}
                        </Typography>
                    </Box>
                    <Box sx={{
                            width: { sm: '50%' },
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            alignSelf: 'center'
                        }}>
                        <Typography variant="body2" sx={{mx: { sm: 2, md: 5 }, textAlign: 'center', padding: 2, fontStyle: 'italic', opacity: 0.8, mb: 2}}>
                            <strong>{t('login.note')}</strong>
                            {t('login.paragraph')}
                        </Typography>
                        {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : ''}
                        <Box component="form" method="POST" onSubmit={loginSubmit}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mx: 2,
                            maxWidth: '100%'
                        }}
                        >
                            <FormControl sx={{ m: 1, width: '25ch' }} variant="outlined"> 
                                <InputLabel htmlFor="username">{t('login.username')}</InputLabel>
                                <OutlinedInput 
                                    required
                                    margin="normal"
                                    id="username"
                                    name="username"
                                    label={t('login.username')} 
                                    variant="outlined"
                                    autoCapitalize="none"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    sx={{backgroundColor: 'white', borderRadius: 2}}
                                />
                            </FormControl>
                            <FormControl sx={{ m: 1, width: '25ch' }} variant="outlined">
                                <InputLabel htmlFor="password">{t('login.password')}</InputLabel>
                                <OutlinedInput 
                                    required
                                    margin="normal"
                                    id="password"
                                    name="password"
                                    label={t('login.password')}
                                    type={showPassword ? "text" : "password"}
                                    autoCapitalize="none"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    sx={{backgroundColor: 'white'}}
                                    endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleTogglePassword}
                                            edge="end"
                                            aria-label="toggle password visibility"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                    }
                                />
                            </FormControl>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                        >
                            {t('login.login_button')}
                        </Button>
                        </Box>
                    </Box>
                </Box>
            </div>
        </Fade>
    )
}

export default Login;